import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FlatpickrDirective, FlatpickrModule } from 'angularx-flatpickr';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModuleStatusOption,
  ListModuleToolbarConfig,
  listModuleFilterEnabled,
} from './list-module.types';

const DEFAULT_STATUS_OPTIONS: ListModuleStatusOption[] = [
  { value: '', label: 'All status' },
  { value: 'Active', label: 'Active' },
  { value: 'InActive', label: 'Inactive' },
];

@Component({
  selector: 'app-list-module-toolbar',
  standalone: true,
  imports: [CommonModule, FormsModule, IconModule, FlatpickrModule],
  templateUrl: './list-module-toolbar.component.html',
})
export class ListModuleToolbarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() config: ListModuleToolbarConfig = {};
  @Input() filters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  /** When true, uses `deletedPlaceholder` variants for search/date labels. */
  @Input() deletedView = false;

  @Output() filtersChange = new EventEmitter<Partial<ListModuleFilters>>();

  @ViewChild('dateRangePicker') dateRangeFlatpickr?: FlatpickrDirective;

  searchExpanded = false;

  private readonly searchSubject = new Subject<string>();
  private searchDebounceSub?: Subscription;

  ngOnInit(): void {
    this.bindSearchDebounce();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const configChange = changes['config'];
    if (!configChange) {
      return;
    }
    if (configChange.firstChange) {
      this.bindSearchDebounce();
      return;
    }
    const prevKey = this.searchDebounceConfigKey(configChange.previousValue);
    const nextKey = this.searchDebounceConfigKey(configChange.currentValue);
    if (prevKey !== nextKey) {
      this.bindSearchDebounce();
    }
  }

  ngOnDestroy(): void {
    this.searchDebounceSub?.unsubscribe();
    this.searchSubject.complete();
  }

  get showSearch(): boolean {
    return listModuleFilterEnabled(this.config.search);
  }

  get showStatus(): boolean {
    return listModuleFilterEnabled(this.config.status);
  }

  get showDateRange(): boolean {
    return listModuleFilterEnabled(this.config.dateRange);
  }

  get searchPlaceholder(): string {
    const cfg = this.config.search;
    if (!cfg || cfg === true) {
      return this.deletedView ? 'Search deleted…' : 'Search…';
    }
    return this.deletedView
      ? cfg.deletedPlaceholder ?? cfg.placeholder ?? 'Search deleted…'
      : cfg.placeholder ?? 'Search…';
  }

  get dateRangePlaceholder(): string {
    const cfg = this.config.dateRange;
    if (!cfg || cfg === true) {
      return this.deletedView ? 'Deleted date range' : 'Date range';
    }
    return this.deletedView
      ? cfg.deletedPlaceholder ?? cfg.placeholder ?? 'Deleted date range'
      : cfg.placeholder ?? 'Date range';
  }

  get statusOptions(): ListModuleStatusOption[] {
    const cfg = this.config.status;
    if (!cfg || cfg === true) {
      return DEFAULT_STATUS_OPTIONS;
    }
    return cfg.options?.length ? cfg.options : DEFAULT_STATUS_OPTIONS;
  }

  get hasActiveDateFilter(): boolean {
    return !!(
      this.filters.dateRange?.trim() ||
      this.filters.dateRangeStart ||
      this.filters.dateRangeEnd
    );
  }

  onSearchInput(): void {
    if (!this.showSearch) return;
    const cfg = this.config.search;
    const debounceMs =
      cfg && cfg !== true && typeof cfg.debounceMs === 'number' ? cfg.debounceMs : 450;
    if (debounceMs <= 0) {
      this.emitFilters({ searchQuery: this.filters.searchQuery });
      return;
    }
    this.searchSubject.next(this.filters.searchQuery);
  }

  submitSearchMobile(ev: Event): void {
    ev.preventDefault();
    this.searchExpanded = false;
    this.emitFilters({ searchQuery: this.filters.searchQuery });
  }

  onStatusChange(): void {
    this.emitFilters({ status: this.filters.status });
  }

  onDateRangeChange(event: { selectedDates: Date[] }): void {
    const d = event.selectedDates ?? [];
    const nextStart = d[0] ? this.formatYmd(d[0]) : '';
    const nextEnd = d[1] ? this.formatYmd(d[1]) : '';
    if (
      nextStart === this.filters.dateRangeStart &&
      nextEnd === this.filters.dateRangeEnd
    ) {
      return;
    }
    this.emitFilters({
      dateRangeStart: nextStart,
      dateRangeEnd: nextEnd,
    });
  }

  clearDateRangeFilter(ev: MouseEvent): void {
    ev.stopPropagation();
    this.dateRangeFlatpickr?.instance?.clear();
    this.emitFilters({
      dateRange: '',
      dateRangeStart: '',
      dateRangeEnd: '',
    });
  }

  /** Only rebind debounce when search enablement or debounceMs changes (not new object refs). */
  private searchDebounceConfigKey(
    config: ListModuleToolbarConfig | undefined,
  ): string {
    const search = config?.search;
    if (!listModuleFilterEnabled(search)) {
      return 'off';
    }
    if (search === true) {
      return 'on:450';
    }
    if (typeof search === 'object') {
      return `on:${search.debounceMs ?? 450}`;
    }
    return 'on:450';
  }

  private bindSearchDebounce(): void {
    this.searchDebounceSub?.unsubscribe();
    const cfg = this.config.search;
    const debounceMs =
      cfg && cfg !== true && typeof cfg.debounceMs === 'number' ? cfg.debounceMs : 450;
    if (!this.showSearch || debounceMs <= 0) {
      return;
    }
    this.searchDebounceSub = this.searchSubject
      .pipe(debounceTime(debounceMs), distinctUntilChanged())
      .subscribe((q) => this.emitFilters({ searchQuery: q }));
  }

  private emitFilters(patch: Partial<ListModuleFilters>): void {
    this.filtersChange.emit(patch);
  }

  private formatYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
